import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Button, Spinner } from '@nextui-org/react';
import {
  Gamepad2,
  Trophy,
  Users,
  Clock,
  Check,
  ChevronRight,
  Star,
  Gift,
  LogIn,
} from 'lucide-react';
import predictionService, {
  PredictionGame,
  MyGameItem,
} from '../services/predictionService';
import { useAuth } from '../contexts/AuthContext';

const ACCENT = '#3fced1';

const PredictionGamesPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [allGames, setAllGames] = useState<PredictionGame[]>([]);
  const [isLoadingAllGames, setIsLoadingAllGames] = useState(true);

  const [myGames, setMyGames] = useState<MyGameItem[]>([]);
  const [isLoadingMyGames, setIsLoadingMyGames] = useState(true);

  const loadAllGames = useCallback(async () => {
    setIsLoadingAllGames(true);
    const games = await predictionService.getAllGames();
    // Wie Flutter: nur aktive Spiele mit Status open/running anzeigen
    const activeGames = games.filter(
      (game) =>
        (game?.status === 'open' || game?.status === 'running') && game?.isActive !== false
    );
    setAllGames(activeGames);
    setIsLoadingAllGames(false);
  }, []);

  const loadMyGames = useCallback(async () => {
    if (!isAuthenticated) {
      setMyGames([]);
      setIsLoadingMyGames(false);
      return;
    }
    setIsLoadingMyGames(true);
    const games = await predictionService.getMyGames();
    setMyGames(games);
    setIsLoadingMyGames(false);
  }, [isAuthenticated]);

  useEffect(() => {
    loadAllGames();
  }, [loadAllGames]);

  useEffect(() => {
    loadMyGames();
  }, [loadMyGames]);

  const openGame = (game: PredictionGame) => {
    const identifier = predictionService.getGameIdentifier(game);
    if (identifier) navigate(`/tippspiele/${identifier}`);
  };

  const isRegisteredForGame = (game: PredictionGame): boolean =>
    myGames.some(
      (item) =>
        (item.game?._id && item.game._id === game._id) ||
        (item.game?.slug && game.slug && item.game.slug === game.slug)
    );

  // ---------- Render-Bausteine ----------

  const renderStatusBadge = (game: PredictionGame) => {
    const info = predictionService.getGameStatusInfo(game);
    return (
      <span
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${info.color} ${info.bgColor}`}
      >
        {info.label}
      </span>
    );
  };

  const renderMyGameItem = (item: MyGameItem) => {
    const game = item.game;
    const points = item.participation?.totalPoints ?? 0;
    const rank = item.participation?.rank ?? null;
    const identifier = predictionService.getGameIdentifier(game);

    return (
      <button
        key={game._id || identifier}
        type="button"
        onClick={() => openGame(game)}
        className="w-full text-left px-5 py-4 border-b border-zinc-800 last:border-b-0 hover:bg-zinc-800/50 transition-colors flex items-center gap-4"
      >
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold truncate">{game.name || 'Tippspiel'}</p>
          {game.tournament?.name && (
            <p className="text-sm text-gray-400 truncate mt-0.5">{game.tournament.name}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-sm font-bold"
            style={{ color: ACCENT, backgroundColor: 'rgba(63, 206, 209, 0.1)' }}
          >
            <Star size={14} />
            {points}
          </span>
          {rank !== null && rank !== undefined && (
            <span
              className="inline-flex items-center px-2.5 py-1.5 rounded-lg text-sm font-bold"
              style={{ color: ACCENT, backgroundColor: 'rgba(63, 206, 209, 0.1)' }}
            >
              #{rank}
            </span>
          )}
        </div>
        <ChevronRight size={16} className="text-gray-600 flex-shrink-0" />
      </button>
    );
  };

  const renderGameItem = (game: PredictionGame) => {
    const registered = isRegisteredForGame(game);
    const registrationEnd = game.registrationEnd
      ? predictionService.formatDate(game.registrationEnd)
      : 'Offen';
    const participants = game.stats?.totalParticipants ?? 0;
    const hasPrizes = Array.isArray(game.prizes) && game.prizes.length > 0;
    const identifier = predictionService.getGameIdentifier(game);

    return (
      <div
        key={game._id || identifier}
        role="button"
        tabIndex={0}
        onClick={() => openGame(game)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') openGame(game);
        }}
        className="px-5 py-4 border-b border-zinc-800 last:border-b-0 hover:bg-zinc-800/50 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-4">
          <div className="flex-1 min-w-0">
            {/* Turniername */}
            {game.tournament?.name && (
              <div className="flex items-center gap-1.5 mb-2">
                <Trophy size={14} style={{ color: ACCENT }} />
                <span className="text-xs font-semibold text-gray-400 truncate">
                  {game.tournament.name}
                </span>
              </div>
            )}

            {/* Name + Status */}
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-white font-semibold text-base">{game.name || 'Tippspiel'}</h3>
              {renderStatusBadge(game)}
              {game.adminOnly && (
                <span className="inline-flex items-center gap-1 rounded-full bg-purple-500/15 px-2 py-0.5 text-[11px] font-bold text-purple-400">
                  Nur Admins
                </span>
              )}
            </div>

            {/* Beschreibung */}
            {game.description && (
              <p className="text-sm text-gray-400 mt-1.5 line-clamp-2">{game.description}</p>
            )}

            {/* Stats-Zeile */}
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 flex-wrap">
              <span className="inline-flex items-center gap-1">
                <Users size={14} />
                {participants} Teilnehmer
              </span>
              <span className="inline-flex items-center gap-1">
                <Clock size={14} />
                Anmeldung bis {registrationEnd}
              </span>
              {predictionService.formatGamePeriod(game) && (
                <span className="inline-flex items-center gap-1">
                  <Trophy size={14} />
                  {predictionService.formatGamePeriod(game)}
                </span>
              )}
              {hasPrizes && (
                <span className="inline-flex items-center gap-1" style={{ color: ACCENT }}>
                  <Gift size={14} />
                  {game.prizes!.length} {game.prizes!.length === 1 ? 'Preis' : 'Preise'}
                </span>
              )}
            </div>
          </div>

          {/* Button rechts */}
          <div className="flex-shrink-0">
            {registered ? (
              <span
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border"
                style={{
                  color: ACCENT,
                  backgroundColor: 'rgba(63, 206, 209, 0.1)',
                  borderColor: 'rgba(63, 206, 209, 0.3)',
                }}
              >
                <Check size={16} />
                Angemeldet
              </span>
            ) : (
              <Button
                size="sm"
                className="font-semibold text-black"
                style={{ backgroundColor: ACCENT }}
                onClick={(e) => {
                  e.stopPropagation();
                  openGame(game);
                }}
              >
                {predictionService.isRegistrationOpen(game) ? 'Mitmachen' : 'Ansehen'}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ---------- Render ----------

  return (
    <>
      <Helmet>
        <title>Tippspiele – Tippe & Gewinne | DartCloud</title>
        <meta
          name="description"
          content="Tippe auf Darts-Turnierergebnisse und gewinne tolle Preise – kostenlose Tippspiele zu PDC-Turnieren auf DartCloud."
        />
        <link rel="canonical" href="https://dartcloudapp.de/tippspiele" />
        <meta property="og:title" content="Tippspiele – Tippe & Gewinne | DartCloud" />
        <meta
          property="og:description"
          content="Tippe auf Darts-Turnierergebnisse und gewinne tolle Preise."
        />
        <meta property="og:url" content="https://dartcloudapp.de/tippspiele" />
      </Helmet>

      <div className="min-h-screen bg-black">
        {/* Hero-Section */}
        <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 border-b border-zinc-800">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-8 md:py-10">
            <div className="flex items-center gap-4">
              <div
                className="p-3 rounded-xl"
                style={{ backgroundColor: 'rgba(63, 206, 209, 0.1)' }}
              >
                <Gamepad2 size={28} style={{ color: ACCENT }} />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-extrabold text-white">Tippspiele</h1>
                <p className="text-sm text-gray-400">Tippe & Gewinne</p>
              </div>
            </div>
            <p className="text-gray-400 mt-5">
              Tippe auf Turnierergebnisse und gewinne tolle Preise
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-[1400px] mx-auto px-5 md:px-12 py-8 space-y-8">
          {/* Meine Tippspiele */}
          {isAuthenticated && (isLoadingMyGames || myGames.length > 0) && (
            <section
              className="bg-zinc-900 rounded-2xl border-2 overflow-hidden"
              style={{ borderColor: 'rgba(63, 206, 209, 0.3)' }}
            >
              <div className="flex items-center gap-3 p-5 border-b border-zinc-800">
                <div className="p-2 rounded-lg" style={{ backgroundColor: ACCENT }}>
                  <Gamepad2 size={20} className="text-black" />
                </div>
                <h2 className="text-xl font-bold text-white">Meine Tippspiele</h2>
              </div>
              {isLoadingMyGames ? (
                <div className="flex justify-center py-8">
                  <Spinner color="default" />
                </div>
              ) : (
                <div>{myGames.map((item) => renderMyGameItem(item))}</div>
              )}
            </section>
          )}

          {/* Login-Hinweis für Gäste */}
          {!isAuthenticated && (
            <section className="bg-zinc-900 rounded-2xl border border-zinc-800 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div
                className="p-2.5 rounded-lg flex-shrink-0"
                style={{ backgroundColor: 'rgba(63, 206, 209, 0.1)' }}
              >
                <LogIn size={20} style={{ color: ACCENT }} />
              </div>
              <div className="flex-1">
                <p className="text-white font-semibold">Jetzt mitmachen!</p>
                <p className="text-sm text-gray-400">
                  Melde dich an, um an Tippspielen teilzunehmen und Preise zu gewinnen.
                </p>
              </div>
              <Button
                as={RouterLink}
                to="/login"
                size="sm"
                className="font-semibold text-black flex-shrink-0"
                style={{ backgroundColor: ACCENT }}
              >
                Anmelden
              </Button>
            </section>
          )}

          {/* Alle Tippspiele */}
          <section className="bg-zinc-900 rounded-2xl border border-zinc-800 overflow-hidden">
            <div className="flex items-center gap-3 p-5 border-b border-zinc-800">
              <div
                className="p-2 rounded-lg"
                style={{ backgroundColor: 'rgba(63, 206, 209, 0.1)' }}
              >
                <Trophy size={20} style={{ color: ACCENT }} />
              </div>
              <h2 className="text-xl font-bold text-white">Alle Tippspiele</h2>
            </div>

            {isLoadingAllGames ? (
              <div className="flex justify-center py-12">
                <Spinner color="default" />
              </div>
            ) : allGames.length === 0 ? (
              <div className="flex flex-col items-center py-12 px-5 text-center">
                <Gamepad2 size={48} className="text-zinc-700 mb-3" />
                <p className="text-gray-500">Keine aktiven Tippspiele verfügbar</p>
              </div>
            ) : (
              <div>{allGames.map((game) => renderGameItem(game))}</div>
            )}
          </section>
        </div>
      </div>
    </>
  );
};

export default PredictionGamesPage;
